import React from 'react';
import { Card, CardBody, Button, Badge } from 'reactstrap';
import { H5 } from '../../../../AbstractElements';

const RewardCard = ({ reward, onEdit, onDelete, onToggleStatus }) => {
  const getLevelBadge = (level) => {
    const badges = {
      excellent: { color: 'success', icon: 'fa-star', text: 'Xuất sắc' },
      good: { color: 'primary', icon: 'fa-thumbs-up', text: 'Tốt' },
      average: { color: 'info', icon: 'fa-check', text: 'Khá' },
      pass: { color: 'warning', icon: 'fa-flag', text: 'Đạt' },
      perfect: { color: 'danger', icon: 'fa-trophy', text: 'Hoàn hảo' }
    };
    const badge = badges[level] || { color: 'secondary', icon: 'fa-medal', text: level };
    
    return (
      <Badge color={badge.color} className="px-3 py-2">
        <i className={`fa ${badge.icon} me-1`}></i>
        {badge.text}
      </Badge>
    );
  };

  const getRewardTypeBadge = (type) => {
    const badges = {
      topup: { color: 'success', icon: 'fa-mobile-alt', text: 'Topup' },
      points: { color: 'primary', icon: 'fa-coins', text: 'Points' },
      badge: { color: 'warning', icon: 'fa-award', text: 'Badge' },
      voucher: { color: 'success', icon: 'fa-ticket-alt', text: 'Voucher' },
      gift: { color: 'info', icon: 'fa-gift', text: 'Gift' },
      coupon: { color: 'dark', icon: 'fa-tags', text: 'Coupon' },
      discount: { color: 'danger', icon: 'fa-percent', text: 'Discount' },
      freeship: { color: 'secondary', icon: 'fa-shipping-fast', text: 'Free Ship' },
      certificate: { color: 'info', icon: 'fa-certificate', text: 'Certificate' }
    };
    const badge = badges[type] || { color: 'secondary', icon: 'fa-star', text: type };
    
    return (
      <Badge color={badge.color}>
        <i className={`fa ${badge.icon} me-1`}></i>
        {badge.text || type}
      </Badge>
    );
  };

  return (
    <Card className="shadow-sm border-0 mb-4">
      <CardBody className="p-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 mb-2">
              <Badge color="secondary" className="px-2">
                <i className="fa fa-sort-numeric-asc me-1"></i>
                #{reward.sortOrder}
              </Badge>
              {reward.level && getLevelBadge(reward.level)}
              {getRewardTypeBadge(reward.rewardType)}
              <Badge color={reward.isActive ? 'success' : 'secondary'}>
                {reward.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <H5 className="text-dark mb-1">{reward.message}</H5>
          </div>
          <div className="d-flex gap-2">
            <Button 
              color="outline-primary" 
              size="sm"
              onClick={() => onEdit(reward)}
            >
              <i className="fa fa-edit"></i>
            </Button>
            <Button 
              color="outline-danger" 
              size="sm"
              onClick={() => onDelete(reward.id)}
            >
              <i className="fa fa-trash"></i>
            </Button>
          </div>
        </div>

        {/* Reward Details */}
        <div className="mb-3 p-3 bg-light rounded">
          <div className="row g-3">
            <div className="col-6">
              <small className="text-muted d-block">Câu trả lời đúng</small>
              <strong className="text-primary">
                {reward.correctAnswers} / {reward.totalQuestions || 'N/A'}
              </strong>
            </div>
            <div className="col-6">
              <small className="text-muted d-block">Số lượng</small>
              <strong className="text-info">
                {(() => {
                  const qty = reward.quantity;
                  
                  // Check if quantity is null or undefined (unlimited)
                  if (qty === null || qty === undefined) {
                    return 'Không giới hạn';
                  }
                  
                  // If it's a number (including 0), show it directly
                  if (typeof qty === 'number') {
                    return qty;
                  }
                  
                  // If it's a string, try to convert to number
                  if (typeof qty === 'string' && qty.trim() === '') {
                    return 'Không giới hạn';
                  }
                  
                  // Try to parse as number
                  const numQty = Number(qty);
                  if (!isNaN(numQty) && isFinite(numQty)) {
                    return numQty;
                  }
                  
                  // Fallback to unlimited
                  return 'Không giới hạn';
                })()}
              </strong>
            </div>
            <div className="col-6">
              <small className="text-muted d-block">Giá trị thưởng</small>
              <strong className="text-success">
                {(() => {
                  if (typeof reward.rewardValue === 'object' && reward.rewardValue !== null) {
                    const rv = reward.rewardValue;
                    switch (reward.rewardType) {
                      case 'topup':
                        return rv.topup ? `${rv.topup.toLocaleString()}đ` : 'Topup';
                      case 'points':
                        return rv.points ? `${rv.points.toLocaleString()} điểm` : 'Points';
                      case 'badge':
                        return rv.badgeName || 'Badge';
                      case 'voucher':
                        return rv.voucherCode || 'Voucher';
                      case 'gift':
                        return rv.giftName || `Quà tặng (${rv.giftValue?.toLocaleString()}đ)`;
                      case 'coupon':
                        return rv.couponCode || 'Coupon';
                      case 'discount':
                        return rv.discountPercent
                          ? `Giảm ${rv.discountPercent}% (tối đa ${rv.maxDiscount?.toLocaleString()}đ)`
                          : 'Discount';
                      case 'freeship':
                        return rv.maxValue
                          ? `Freeship tới ${rv.maxValue.toLocaleString()}đ`
                          : 'Freeship';
                      default:
                        return rv.description || 'N/A';
                    }
                  }
                  if (reward.rewardType === 'points') return `${reward.rewardValue} điểm`;
                  if (reward.rewardType === 'topup') return `${reward.rewardValue}đ`;
                  return reward.rewardValue;
                })()}
              </strong>
            </div>
            <div className="col-12">
              <small className="text-muted d-block">
                {reward.quizTemplate?.name || `Quiz #${reward.quizId}`}
              </small>
              {typeof reward.rewardValue === 'object' && reward.rewardValue?.description && (
                <small className="text-muted d-block mt-1">
                  {reward.rewardValue.description}
                </small>
              )}
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            {reward.createdAt && `Created: ${new Date(reward.createdAt).toLocaleDateString('vi-VN')}`}
          </small>
          <Button
            color={reward.isActive ? 'warning' : 'success'}
            size="sm"
            onClick={() => onToggleStatus(reward.id)}
          >
            <i className={`fa fa-${reward.isActive ? 'eye-slash' : 'eye'} me-1`}></i>
            {reward.isActive ? 'Tắt' : 'Bật'}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

export default RewardCard;

